/**
 * BulkArchiveBar — action bar shown while list rows are selected.
 *
 * The cleanup path for records that predate import-batch tracking (or
 * arrived any other way): filter the list down, tick the junk, archive
 * it in one confirmed action. In the Archived view the same bar
 * restores instead — archive is the reversible archived_at
 * soft-archive, so nothing here is destructive.
 */

import { useState } from 'react';
import { Archive, ArchiveRestore } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface Props {
  mode: 'archive' | 'restore';
  selectedCount: number;
  /** Singular / plural nouns for copy: "contact" / "contacts". */
  noun: string;
  nounPlural: string;
  pending: boolean;
  onConfirm: () => void;
  onClear: () => void;
}

export function BulkArchiveBar({
  mode,
  selectedCount,
  noun,
  nounPlural,
  pending,
  onConfirm,
  onClear,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  if (selectedCount === 0) return null;
  const label = selectedCount === 1 ? noun : nounPlural;

  return (
    <div className="flex items-center gap-3 rounded border border-primary/40 bg-primary/5 px-4 py-2">
      <p className="text-sm font-medium">
        {selectedCount} {label} selected
      </p>
      {mode === 'archive' ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pending}
          onClick={() => setConfirming(true)}
        >
          <Archive className="mr-1 h-3.5 w-3.5" />
          {pending ? 'Archiving…' : 'Archive selected'}
        </Button>
      ) : (
        // Restore is the safe direction — no confirmation needed.
        <Button type="button" variant="outline" size="sm" disabled={pending} onClick={onConfirm}>
          <ArchiveRestore className="mr-1 h-3.5 w-3.5" />
          {pending ? 'Restoring…' : 'Restore selected'}
        </Button>
      )}
      <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onClear}>
        Clear selection
      </Button>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive {selectedCount} {label}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They'll disappear from your lists but aren't deleted — switch to the Archived view any
              time to restore them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                onConfirm();
              }}
            >
              Archive {label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
